// SalesStatisticsPage.tsx
import React, { useState } from "react";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Button, Col, Container, FloatingLabel, Form, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry, ColDef } from "ag-grid-community";
import * as XLSX from "xlsx";
import SalesStatisticsStore from "../stores/SalesStatisticsStore";
import StudioUserStore from "../stores/StudioUserStore";
import BlockKitStore from "../stores/BlockKitStore";
import { SalesCategoryModel, SalesThemeModel } from "../controllers/SalesStatisticsController";
import { getLocaleString } from "../utils/DateHelper";
import { ModeJsonModel, LicenseJsonModel } from "../models/BlockTransportSystemJsonModel";
import { LanguageCode } from "../constants/LanguageCode";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface Props {
  salesStatisticsStore?: SalesStatisticsStore;
  studioUserStore?: StudioUserStore;
  blockKitStore?: BlockKitStore;
}

const SalesStatisticsPage: React.FC<Props> = inject("salesStatisticsStore", "studioUserStore", "blockKitStore")(observer(({ salesStatisticsStore, studioUserStore, blockKitStore }) => {
  const { t } = useTranslation();
  const me = studioUserStore?.me;
  const [modes, setModes] = useState<ModeJsonModel[]>([]);
  const [licenses, setLicenses] = useState<LicenseJsonModel[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SalesCategoryModel | null>(null);

  React.useEffect(() => {
    blockKitStore?.studioController.getUserModesAsync().then(setModes);
    blockKitStore?.studioController.getUserLicensesAsync().then(setLicenses);
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today);
  const [searchedStartDate, setSearchedStartDate] = useState("");
  const [searchedEndDate, setSearchedEndDate] = useState("");
  const [selectedLocale, setSelectedLocale] = useState<LanguageCode | "">("");
  const [selectedModeId, setSelectedModeId] = useState<string>("");
  const [selectedLicenseId, setSelectedLicenseId] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<SalesCategoryModel[]>([]);
  const [selectedThemeRows, setSelectedThemeRows] = useState<SalesThemeModel[]>([]);
  const gridRef = React.useRef<AgGridReact>(null);

  React.useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [blockKitStore?.timeZone]);

  const handleCategoryClick = (category: SalesCategoryModel) => {
    setSelectedThemeRows([]);
    setSelectedCategory(category);
  };

  const isDateRangeValid = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return diffMonths < 3 || (diffMonths === 3 && end.getDate() <= start.getDate());
  };

  const handleSearch = async () => {
    if (!isDateRangeValid()) {
      alert(t("date-range-limit"));
      return;
    }
    setSelectedCategory(null);
    setSelectedRows([]);
    setSelectedThemeRows([]);
    await salesStatisticsStore?.fetchAsync({
      startDate,
      endDate,
      locale: selectedLocale,
      modeId: selectedModeId,
      licenseId: selectedLicenseId,
    });
    setSearchedStartDate(startDate);
    setSearchedEndDate(endDate);
  };

  // 카테고리별 탭 분리 엑셀 다운로드
  const handleExcelDownload = () => {
    const wb = XLSX.utils.book_new();
    const prefix = t("nav-sales-statistics");
    const fileName = `${prefix}_${searchedStartDate}_${searchedEndDate}.xlsx`;
    // xlsx 시트명 금지 문자 치환
    const sanitizeSheetName = (name: string) => name.replace(/[:\\/?*[\]]/g, "_");

    // 카테고리 상세(테마)
    if (selectedCategory) {
      // selectedThemeRows 기준으로 저장
      if (selectedThemeRows.length === 0) {
        alert(t("no-selected"));
        return;
      }
      const rows = selectedThemeRows.map((theme: SalesThemeModel) => ({
        [t("col-product-type")]: theme.productType,
        [t("col-block-type-detail")]: theme.blockType,
        [t("col-name")]: theme.name,
        [t("col-license")]: theme.licenseName,
        [t("col-total-count")]: theme.totalCount,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(selectedCategory.categoryName).slice(0, 31));
      XLSX.writeFile(wb, fileName);
      return;
    }

    if (selectedRows.length === 0) {
      alert(t("no-selected"));
      return;
    }

    // 첫 번째 시트: 요약 (화면 컬럼 그대로)
    const summaryRows = selectedRows.map((category: SalesCategoryModel) => ({
      [t("col-category-name")]: category.categoryName,
      [t("col-block-type")]: category.blockType,
      [t("col-block-count")]: category.blockCount,
      [t("col-license")]: category.licenseName,
      [t("col-total-count")]: category.totalCount,
      [t("col-release-date")]: getLocaleString(new Date(category.releaseDate)),
      [t("col-expire-date")]: getLocaleString(new Date(category.expireDate)),
    }));
    const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
    XLSX.utils.book_append_sheet(wb, summaryWs, t("sheet-summary").slice(0, 31));

    const sheetNameCount: Record<string, number> = {};
    // 이후 시트: 카테고리별 테마별 상세
    selectedRows.forEach((category: SalesCategoryModel) => {
      const rows = category.themes.map((theme: SalesThemeModel) => ({
        [t("col-title")]: theme.name,
        [t("col-category")]: theme.categoryName,
        [t("col-product-type")]: theme.productType,
        [t("col-block-type")]: theme.blockType,
        [t("col-license")]: theme.licenseName,
        [t("col-total-count")]: theme.totalCount,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);

      const baseName = sanitizeSheetName(category.categoryName).slice(0, 28);
      const count = sheetNameCount[baseName] ?? 0;
      const sheetName = count === 0 ? baseName : `${baseName}_${count}`;
      sheetNameCount[baseName] = count + 1;

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
    XLSX.writeFile(wb, fileName);
  };

  const PreviewCell = (props: ICellRendererParams) => (
    props.value ? (
      <a href={props.value} target="_blank" rel="noopener noreferrer">
        <img src={props.value} alt="배너" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </a>
    ) : null
  );

  const CategoryNameCell = (props: ICellRendererParams) => (
    <span
      style={{ color: "blue", cursor: "pointer" }}
      onClick={() => handleCategoryClick(props.data)}
    >
      {props.value}
    </span>
  );

  return (
    <Container fluid style={{ backgroundColor: "#F6F7F8", minHeight: "100vh" }}>
      <style>{`
        .ag-header-cell-label { justify-content: center; }
      `}</style>

      {/* 필터 */}
      <Row className="g-2 mb-2 pt-3 fs-6">
        <Col md>
        {/* 조회 시작일 */}
          <FloatingLabel label={t("start-date")}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 조회 종료일 */}
          <FloatingLabel label={t("end-date")}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            
            />
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 국가 */}
          <FloatingLabel label={t("locale")}>
            <Form.Select onChange={e => setSelectedLocale(e.target.value as LanguageCode | "")}>
              <option value="">ALL</option>
              {me?.allowedLocales.map(locale => (
                <option key={locale} value={locale}>{locale}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 고객사(스크린) */}
          <FloatingLabel label={t("client")}>
            <Form.Select onChange={e => setSelectedModeId(e.target.value)}>
              <option value="">ALL</option>
              {modes.map(mode => {
                return (
                  <option key={mode.id} value={mode.id}>
                    {mode?.name ?? mode.id}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 라이선스 */}
          <FloatingLabel label={t("license")}>
            <Form.Select value={selectedLicenseId} onChange={e => setSelectedLicenseId(e.target.value)}>
              <option value="all">ALL</option>
              <option value="">{t("license-none")}</option>
              {me?.allowedLicenseIds.filter(Boolean).map(licenseId => {
                const license = licenses.find(l => l.id === licenseId);
                return (
                  <option key={licenseId} value={licenseId}>
                    {license?.name ?? licenseId}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md="auto" className="d-flex justify-content-center align-items-center">
        {/* "조회중..." : "검색" */}
          <Button size="sm" onClick={handleSearch} disabled={salesStatisticsStore?.isLoading}>
            {salesStatisticsStore?.isLoading ? t("searching") : t("search")}
          </Button>
        </Col>
      </Row>

    <div style={{ backgroundColor: "#fff", borderRadius: "8px", padding: "12px 16px" }}>
      {/* 조회 기간 + 엑셀 버튼 */}
        <Row className="mb-2 align-items-center">
            <Col>
                {selectedCategory ? (
                <>
                    <span className="fw-bold" translate="no">
                    {selectedCategory.categoryName} / 
                    </span>
                    {/* 조회 기간 내 출력합계 : 총 */}
                    <span className="fw-bold ms-2" translate="no">
                    {t("total-count-label", { count: selectedCategory.totalCount })}
                    </span>
                </>
                ) : (
                searchedStartDate && searchedEndDate && (
                    <span className="fw-bold" translate="no">
                    {searchedStartDate} ~ {searchedEndDate}
                    </span>
                )
                )}
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                size="sm"
                variant="success"
                onClick={handleExcelDownload}
              >
                Excel
              </Button>
            </Col>
        </Row>

      {/* 카테고리 클릭 시 뒤로가기 */}
        {selectedCategory && (
        <Row className="mb-2 align-items-center">
            <Col className="d-flex justify-content-start">
            {/* 목록으로 */}
            <Button
              variant="link"
              size="sm"
              className="text-muted p-0"
              onClick={() => setSelectedCategory(null)}
            >
              ⬅{t("back-to-list")}
            </Button>
            </Col>
        </Row>
        )}

      {/* 테이블 */}
      <div style={{ height: "70vh" }}>
       {selectedCategory ? (
        <AgGridReact<SalesThemeModel>
          getRowId={params => params.data.id}
          rowData={selectedCategory.themes}
          defaultColDef={{
            flex: 1,
            cellStyle: { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }
          }}
          rowSelection={{ mode: "multiRow" }}
          onSelectionChanged={e => setSelectedThemeRows(e.api.getSelectedRows())} 
          columnDefs={[
            {
              headerName: t("col-preview"), // 배너
              field: "previewUrl",
              width: 90,
              cellRenderer: PreviewCell,
            },
            {
              headerName: t("col-product-type"), // 상품타입
              field: "productType",
              filter: true,
            },
            {
              headerName: t("col-block-type-detail"), // 노출제품(전용제품설정)
              field: "blockType",
              filter: true,
            },
            {
              headerName: t("col-name"), // 이름
              field: "name",
              filter: true,
            },
            {
              headerName: t("col-license"), // 라이선스
              field: "licenseName",
              filter: true,
            },
            {
              headerName: t("col-total-count"), // 출력수량
              field: "totalCount",
              filter: true,
            },
          ]}
        />
      ) : (
        <AgGridReact<SalesCategoryModel>
          ref={gridRef}
          getRowId={params => params.data.categoryId}
          rowData={salesStatisticsStore?.data}
          defaultColDef={{
            flex: 1,
            cellStyle: { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }
          }}
          rowSelection={{ mode: "multiRow" }}
          onSelectionChanged={e => setSelectedRows(e.api.getSelectedRows())}
          columnDefs={[
            {
              headerName: t("col-preview"), // 배너
              field: "previewUrl",
              width: 90,
              cellRenderer: PreviewCell,
            },
            {
              headerName: t("col-category-name"), // 타이틀(카테고리)
              field: "categoryName",
              filter: true,
              cellRenderer: CategoryNameCell,
            },
            {
              headerName: t("col-block-type"), // 디자인종류(타입)
              field: "blockType",
              filter: true,
            },
            {
              headerName: t("col-block-count"), // 이미지수량
              field: "blockCount",
              width: 120,
              filter: true,
            },
            {
              headerName: t("col-license"), // 라이선스
              field: "licenseName",
              filter: true,
            },
            {
              headerName: t("col-total-count"), // 출력수량
              field: "totalCount",
              width: 120,
              filter: true,
            },
            {
              headerName: t("col-release-date"), // 공개날짜
              field: "releaseDate",
              filter: "agDateColumnFilter",
              valueFormatter: (params: any) => getLocaleString(new Date(params.data.releaseDate), blockKitStore?.timeZone),
            },
            {
              headerName: t("col-expire-date"), // 만료날짜
              field: "expireDate",
              filter: "agDateColumnFilter",
              valueFormatter: (params: any) => getLocaleString(new Date(params.data.expireDate), blockKitStore?.timeZone),
            },
          ]}
        />
      )}
      </div>
    </div>
    </Container>
  );
}));

export default SalesStatisticsPage;